/**
 * №22.6 (H2 / O1) — THE ANSWERED STRIP. One component, every home it belongs in.
 *
 * David, closing №22.5: *"I clicked a few of them, they appeared to progress…
 * navigated away and back, and all the Needs-You cards are gone. Where did they
 * go!?"* — the receipts existed, in each task's thread, and the inbox-local one
 * fades on next visit by design (D-CV1). So the surface where the work happened
 * kept no account of it, and **a legitimate clearing and a data loss looked
 * identical.**
 *
 * This is the account. It is deliberately ONE component used in two places —
 * "answered today" on the Needs-You home, and "every call made on this matter" on
 * the record — for the same reason there is ONE decision card (D-CC4): two
 * implementations of a history are two histories.
 *
 * Everything rendered here is a real stored row. The one-line account (`line`) is
 * composed server-side so a host reading `list_answered_decisions` through the
 * connector reads the same sentence this strip shows.
 */
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";
import type { AnsweredDecision } from "../../../lib/types";

/** The mark each outcome earns. `✓` is reserved for a call that actually ran —
 *  a failed execution and a skip are NOT ticks, which is 22.4's whole D-HM1
 *  point applied to history: a mark that flatters is worse than no mark. */
const OUTCOME_MARK: Record<AnsweredDecision["outcome"], { glyph: string; tone: string; word: string }> = {
  executed: { glyph: "✓", tone: "var(--ok)", word: "answered" },
  recorded: { glyph: "✓", tone: "var(--ok)", word: "decided" },
  replied: { glyph: "✓", tone: "var(--ok)", word: "answered in words" },
  skipped: { glyph: "⊘", tone: "var(--text-3)", word: "skipped" },
  failed: { glyph: "✗", tone: "var(--danger)", word: "did not run" },
};

function when(iso: string): string {
  const t = Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(t)) return "";
  return new Date(t).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
}

export interface AnsweredStripProps {
  /** Per matter — the walk: "show me every call made on this matter". */
  anchorType?: string;
  anchorNo?: string;
  /** Per run. */
  instanceNo?: number;
  /** Per hire. */
  agentId?: string;
  /** ISO lower bound — the home passes today's midnight for "Answered today". */
  since?: string;
  limit?: number;
  heading: string;
  /** The section's sub-line. */
  note: string;
  /**
   * When true the section renders nothing at all if the ledger is empty (the
   * home: an empty day should not grow a heading). The record page passes false,
   * because "no calls have been made on this matter" is itself an answer to the
   * question being asked, and an honest empty state beats a missing section.
   */
  hideWhenEmpty?: boolean;
}

export function AnsweredStrip(props: AnsweredStripProps) {
  const { anchorType, anchorNo, instanceNo, agentId, since, limit = 25 } = props;
  const q = useQuery<AnsweredDecision[]>({
    queryKey: ["answered-decisions", anchorType ?? "", anchorNo ?? "", instanceNo ?? 0, agentId ?? "", since ?? ""],
    // Shares no cache with the inbox on purpose: answering INVALIDATES this (see
    // use-task-thread), so the account appears the moment the call is made rather
    // than on the next poll — the trust moment D-CV1 is built around.
    staleTime: 5_000,
    queryFn: () => mcpClient.listAnsweredDecisions({
      ...(anchorType ? { anchor_type: anchorType } : {}),
      ...(anchorNo ? { anchor_no: anchorNo } : {}),
      ...(instanceNo ? { instance_no: instanceNo } : {}),
      ...(agentId ? { agent_id: agentId } : {}),
      ...(since ? { since } : {}),
      limit,
    }),
  });
  const rows = q.data ?? [];
  if (q.isLoading) return null;
  if (rows.length === 0 && props.hideWhenEmpty) return null;

  return (
    <>
      <div className="sechead">
        <h2>{props.heading}</h2>
        <span className="n">{rows.length ? props.note : "nothing yet"}</span>
        <div className="rule" />
      </div>
      {rows.length === 0 ? (
        <div className="surface"><div className="surface__section"><div className="empty-state">
          <h3>No calls have been made here yet</h3>
          <p>When someone answers a decision on this record — a choice, a skip, or a reply in their own words — it is recorded here with what was asked, what was chosen, and the evidence that was on the card at the time.</p>
        </div></div></div>
      ) : (
        <div className="surface"><div className="surface__section">
          <table className="ledger-table">
            <thead><tr><th>What was asked</th><th>Outcome</th><th>By</th><th>When</th><th>Consequence</th></tr></thead>
            <tbody>
              {rows.map((a) => {
                const mark = OUTCOME_MARK[a.outcome];
                return (
                  <tr key={a.id}>
                    <td>
                      {a.question || "(the question was not recorded)"}
                      {/* Where it happened — omitted on a per-matter read, where
                          every row shares the same anchor and repeating it is noise. */}
                      {!anchorNo && (a.anchorName || a.anchorNo) ? <span className="muted"> — {a.anchorName || a.anchorNo}</span> : null}
                      {a.agentName ? <span className="muted"> · raised by {a.agentName}</span> : null}
                    </td>
                    <td>
                      <span style={{ color: mark.tone, fontWeight: 700 }} aria-hidden="true">{mark.glyph}</span>{" "}
                      {a.optionLabel ? a.optionLabel : mark.word}
                    </td>
                    <td>{a.answeredByName?.trim() || "you"}</td>
                    <td className="row-date">{when(a.answeredAt)}</td>
                    {/* The consequence the choice STATED, kept verbatim — this is the
                        column that makes the table audit rather than telemetry. */}
                    <td>{a.consequence ? <span className="muted">{a.consequence}</span> : <span className="muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div></div>
      )}
    </>
  );
}
